import React from 'react';

interface CompilerErrorDialogProps {
    isOpen: boolean;
    onClose: () => void;
    diagnostics: Array<{
        severity: number;
        message: string;
        code?: string;
        source?: string;
        range?: {
            start: { line: number; character: number };
            end: { line: number; character: number };
        };
    }>;
}

const CompilerErrorDialog: React.FC<CompilerErrorDialogProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     diagnostics
                                                                 }) => {
    if (!isOpen) return null;

    const formatLocation = (range?: CompilerErrorDialogProps['diagnostics'][0]['range']) => {
        if (!range) return '';
        return `Line ${range.start.line + 1}, Character ${range.start.character + 1}`;
    };

    // Style definitions inline like MotokoPackagesDialog
    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        },
        content: {
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        title: {
            fontSize: '24px',
            marginBottom: '24px',
            color: '#4B5320',
            fontWeight: '600',
        },
        packageList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        },
        packageItem: {
            padding: '16px',
            borderRadius: '6px',
            backgroundColor: '#f8f8f8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
        },
        packageInfo: {
            flex: 1,
        },
        packageName: {
            fontWeight: '500',
            color: '#4B5320',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        packageDescription: {
            fontSize: '14px',
            color: '#666',
            marginTop: '4px',
        },
        severity: {
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            marginLeft: '8px'
        },
        severityError: {
            backgroundColor: '#FEE2E2',
            color: '#DC2626'
        },
        severityWarning: {
            backgroundColor: '#FEF3C7',
            color: '#D97706'
        },
        sourceInfo: {
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#666',
            marginTop: '8px'
        },
        footer: {
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: '#8B4513',
            color: 'white',
            transition: 'background-color 0.2s'
        }
    } as const;

    return (
        <section style={styles.overlay as any}>
            <section style={styles.content as any}>
                <h2 style={styles.title}>Compilation Issues</h2>

                <section style={styles.packageList}>
                    {diagnostics.map((diagnostic, index) => (
                        <article key={index} style={styles.packageItem}>
                            <section style={styles.packageInfo}>
                                <header style={styles.packageName}>
                                    {diagnostic.severity === 1 ? (
                                        <span style={{...styles.severity, ...styles.severityError}}>Error</span>
                                    ) : (
                                        <span style={{...styles.severity, ...styles.severityWarning}}>Warning</span>
                                    )}
                                    {diagnostic.code && (
                                        <span style={styles.packageDescription}>
                                            Code: {diagnostic.code}
                                        </span>
                                    )}
                                </header>

                                <p style={styles.packageDescription}>
                                    {diagnostic.message}
                                </p>

                                {diagnostic.source && (
                                    <p style={styles.sourceInfo}>
                                        {diagnostic.source}
                                        {diagnostic.range && ` at ${formatLocation(diagnostic.range)}`}
                                    </p>
                                )}
                            </section>
                        </article>
                    ))}
                </section>

                <footer style={styles.footer}>
                    <button
                        onClick={onClose}
                        style={styles.button}
                    >
                        Close
                    </button>
                </footer>
            </section>
        </section>
    );
};

export default CompilerErrorDialog;